import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { MailQuestion } from "lucide-react";

export default function ForgotPasswordLink({ role = "customer", trigger }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim(), role });
      setSent(true);
      toast.success("If an account matches, we've sent a reset link.");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSent(false);
    setEmail("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <button type="button" data-testid="forgot-password-link"
                  className="text-xs underline"
                  style={{ color: "var(--text-muted)" }}>
            Forgot password?
          </button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailQuestion className="h-5 w-5 gold" />
            Reset your password
          </DialogTitle>
        </DialogHeader>
        {sent ? (
          <div className="py-4 text-sm" style={{ color: "var(--text-muted)" }}>
            If an account matches <span className="font-medium" style={{ color: "var(--text)" }}>{email}</span>,
            we&apos;ve emailed a reset link. It expires in 60 minutes.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="fp-email">
                {role === "customer" ? "Email on your account" : "Staff username or email"}
              </Label>
              <Input id="fp-email" data-testid="forgot-email-input"
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder={role === "customer" ? "you@example.com" : "admin"}
                     required />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={loading}
                      data-testid="forgot-submit-btn"
                      className="rounded-full text-white"
                      style={{ background: "var(--navy)" }}>
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
